import React, { MutableRefObject, useEffect, useState } from 'react';
import Head from 'next/head';
import { tw } from 'twind';
import { css } from 'twind/css';

import { useSiteConfig } from 'hooks/useSiteConfig';
import { getVideoPosterSrc } from 'utils/getVideoPosterSrc';

import { VideoPlayerWithGA4Events } from './VideoPlayerWithGA4Events';

type ProductVideoOptions = {
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
};

export interface VideoProps extends React.ComponentProps<'video'> {
  className?: string;
  playsInline?: boolean;
  videoOptions: ProductVideoOptions;
  priority?: boolean;
  videoStyle?: ReturnType<typeof css>;
  mobileSrc?: string;
}

export type ProductVideo = {
  __typename: string;
  src: string;
  mobileSrc?: string;
  videoOptions: ProductVideoOptions;
  muted?: boolean;
  videoStyle?: ReturnType<typeof css>;
};

export const Video = React.forwardRef<HTMLVideoElement, VideoProps>(
  (props, ref) => {
    const {
      src,
      mobileSrc,
      videoStyle,
      videoOptions,
      className = 'zoom-video',
      playsInline = true,
      muted,
      priority = false,
    } = props;
    const isMuted = Boolean(muted || videoOptions.muted);

    const videoRef = ref as MutableRefObject<HTMLVideoElement> | null;

    const [isPosterVisible, setIsPosterVisible] = useState(true);

    const { env } = useSiteConfig();

    const poster = getVideoPosterSrc({
      src: src,
      sanityDataset: env?.sanityDataset,
      sanityProjectId: env?.sanityProjectId,
    });

    const mobilePoster = getVideoPosterSrc({
      src: mobileSrc,
      sanityDataset: env?.sanityDataset,
      sanityProjectId: env?.sanityProjectId,
    });

    useEffect(() => {
      if (videoRef?.current && (src || mobileSrc)) {
        videoRef.current.load();
      }
    }, [src, mobileSrc, videoRef]);

    useEffect(() => {
      // React does not guarantee setting attributes on <video /> elements
      // https://github.com/facebook/react/issues/10389

      if (
        videoRef?.current &&
        videoRef.current.hasAttribute('muted') !== isMuted
      ) {
        if (isMuted) {
          videoRef?.current.setAttribute('muted', '');
        } else {
          videoRef.current.removeAttribute('muted');
        }
      }
    }, [videoOptions, videoRef, isMuted]);

    useEffect(() => {
      const videoElement = videoRef?.current; // Capture the current value.

      if (!videoElement) {
        return; // Exit early if the video element is not yet available.
      }

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && videoOptions.autoPlay) {
              // Video is in view, play it.
              videoElement.play().catch(error => {
                // eslint-disable-next-line no-console
                console.error('Video autoplay failed:', error);
              });
            } else {
              // Video is out of view, pause it.
              videoElement.pause();
            }
          });
        },
        {
          root: null,
          threshold: 0.5,
        }
      );

      observer.observe(videoElement);

      return () => {
        observer.unobserve(videoElement);
        observer.disconnect();
      };
    }, [videoRef, videoOptions.autoPlay]);

    useEffect(() => {
      const videoElement = videoRef?.current;
      if (videoElement && isPosterVisible) {
        // Only act if poster is currently visible
        const handleVideoPlaying = () => {
          setIsPosterVisible(false);
        };
        videoElement.addEventListener('playing', handleVideoPlaying);
        return () => {
          videoElement.removeEventListener('playing', handleVideoPlaying);
        };
      }
    }, [videoRef, isPosterVisible]);

    return (
      <>
        <Head>
          {priority && poster && (
            <link
              rel="preload"
              as="image"
              href={poster}
              fetchpriority="high"
            ></link>
          )}
          {priority && mobilePoster && (
            <link
              rel="preload"
              as="image"
              href={mobilePoster}
              fetchpriority="high"
            ></link>
          )}
        </Head>
        <div className={tw(videoStyle)}>
          {isPosterVisible &&
            poster &&
            mobilePoster &&
            poster !== mobilePoster && (
              <picture className="video-poster-picture">
                <source media="(min-width: 768px)" srcSet={poster} />

                <source media="(max-width: 767px)" srcSet={mobilePoster} />
                <img
                  src={poster || mobilePoster}
                  alt="Video preview"
                  className="video-poster-img"
                  fetchpriority={priority ? 'high' : undefined}
                  loading={priority ? 'eager' : 'lazy'}
                />
              </picture>
            )}
          <VideoPlayerWithGA4Events
            ref={videoRef}
            className={tw(className)}
            controls={videoOptions.controls}
            autoPlay={videoOptions.autoPlay && priority}
            loop={videoOptions.loop}
            muted={isMuted}
            playsInline={playsInline}
            controlsList="nodownload"
            poster={
              !(poster && mobilePoster && poster !== mobilePoster)
                ? poster || mobilePoster
                : undefined
            }
            preload={priority ? 'auto' : 'none'}
          >
            {src && mobileSrc && src !== mobileSrc ? (
              <>
                <source
                  key={src}
                  src={src}
                  type="video/mp4"
                  data-test-id="video-source"
                  media="(min-width: 768px)"
                />
                <source
                  key={mobileSrc}
                  src={mobileSrc}
                  type="video/mp4"
                  data-test-id="mobile-video-source"
                  media="(max-width: 767px)"
                />
              </>
            ) : (
              <source
                key={src || mobileSrc}
                src={src || mobileSrc}
                type="video/mp4"
                data-test-id="video-source"
              />
            )}
          </VideoPlayerWithGA4Events>
        </div>
      </>
    );
  }
);
Video.displayName = 'Video';
